import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../../shared/components/Sidebar";
import BackButton from "../../../shared/components/Backbtn";
import { Button, CircularProgress, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../context/Firebase";
import FirebaseBiodataRepository from "../api/FirebaseBiodataRepository";
import DeleteBiodataUseCase from "../use-cases/DeleteBiodataUseCase";
import UpdateBiodataStatusUseCase from "../use-cases/UpdateBiodataStatusUseCase";
import { getDefaultImage } from "../../../shared/utils/imageHelpers";
import "./biodata.css";

const BiodataDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [loading, setLoading] = useState(true);

  // Fetch specific profile by ID
  useEffect(() => {
    if (!id) return;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const profileDoc = await getDoc(doc(db, "marriage_profiles", id));
        if (profileDoc.exists()) {
          setProfile({ id: profileDoc.id, ...profileDoc.data() });
        } else {
          setProfile(null);
          setSnackbarMessage("Profile not found");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          // Redirect after a short delay to allow user to see the message
          setTimeout(() => navigate("/biodata"), 2000);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setSnackbarMessage("Failed to load profile");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        const biodataRepository = new FirebaseBiodataRepository();
        const deleteBiodataUseCase = new DeleteBiodataUseCase(biodataRepository);
        await deleteBiodataUseCase.execute(id);
        setSnackbarMessage("Profile deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => navigate("/biodata"), 1500);
      } catch (err) {
        console.error("Error deleting profile:", err);
        setSnackbarMessage("Failed to delete profile");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const biodataRepository = new FirebaseBiodataRepository();
      const updateBiodataStatusUseCase = new UpdateBiodataStatusUseCase(biodataRepository);
      await updateBiodataStatusUseCase.execute(id, newStatus);
      setSnackbarMessage("Profile status updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setProfile((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Error updating status:", err);
      setSnackbarMessage("Failed to update profile status");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const allImages = profile?.imagePaths?.length > 0
    ? profile.imagePaths
    : [getDefaultImage(profile?.Gender ?? profile?.gender)];

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleCloseImagePopup = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const formatValue = (key, value) => {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    if (value?.seconds) { // Firestore timestamp
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "N/A";
    }
    if (typeof value === "object" && value !== null) {
      return "N/A"; // Don't show raw objects
    }
    return value ?? "N/A";
  };

  // Organize profile data into sections
  const getProfileSections = () => {
    if (!profile) return {};

    return {
      "Personal Information": {
        "Full Name": profile['Full Name'] ?? profile.fullName,
        "Gender": profile.Gender ?? profile.gender,
        "Date of Birth": profile.Dob ?? profile.dob,
        "Age Range": profile['Age Range'] ?? profile.ageRange,
        "Height": profile.Height ?? profile.height,
        "Marital Status": profile['Marital Status'] ?? profile.maritalStatus,
        "Mother Tongue": profile['Mother Tongue'] ?? profile.motherTongue,
        "Caste": profile.Caste ?? profile.caste,
        "Gotra": profile.Gotra ?? profile.gotra,
        "Mangal": profile.Mangal ?? profile.mangal,
      },
      "Professional Details": {
        "Occupation": profile.Occupation ?? profile.occupation,
        "Qualification": profile.Qualification ?? profile.qualification,
        "Workplace": profile.Workplace ?? profile.workplace,
        "Job Type": profile['Job Type'] ?? profile.jobType,
        "Income": profile.Income ?? profile.income,
      },
      "Family Information": {
        "Father Name": profile['Father Name'] ?? profile.fatherName,
        "Father Occupation": profile['Father Occupation'] ?? profile.fatherOccupation,
        "Father Brothers": profile['Father Brothers'] ?? profile.fatherBrothers,
        "Mother Name": profile['Mother Name'] ?? profile.motherName,
        "Mother Occupation": profile['Mother Occupation'] ?? profile.motherOccupation,
        "Mother Brothers": profile['Mother Brothers'] ?? profile.motherBrothers,
        "Family Type": profile['Family Type'] ?? profile.familyType,
        "Family Income": profile['Family Income'] ?? profile.familyIncome,
        "Total Siblings": profile['Total Siblings'] ?? profile.totalSiblings,
        "Married Brothers": profile['Married Brothers'] ?? profile.marriedBrothers,
        "Unmarried Brothers": profile['Unmarried Brothers'] ?? profile.unmarriedBrothers,
        "Married Sisters": profile['Married Sisters'] ?? profile.marriedSisters,
        "Unmarried Sisters": profile['Unmarried Sisters'] ?? profile.unmarriedSisters,
      },
      "Personal Details": {
        "About": profile.About ?? profile.about,
        "Hobbies": profile.Hobbies ?? profile.hobbies,
        "Food Preferences": profile['Food Preferences'] ?? profile.foodPreferences,
        "Personal Habits": profile['Personal Habits'] ?? profile.personalHabits,
      },
      "Contact Information": {
        "Mobile": profile.Mobile ?? profile.mobile,
        "Address": profile.Address ?? profile.address,
        "Hometown": profile.Hometown ?? profile.hometown,
      }
    };
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <p>Profile not found</p>
          <Button variant="contained" onClick={() => navigate("/biodata")}>
            Back to Biodata List
          </Button>
        </div>
      </div>
    );
  }

  return (
  <>
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content" style={{ maxHeight: '100vh', overflowY: 'auto' }}>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <div className="flex justify-end gap-3 mb-4" style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--card-bg)', paddingBottom: '10px' }}>
          <BackButton tooltip="Back" />
          <Button variant="outlined" color="primary" onClick={() => navigate(`/edit-biodata/${profile.id}`)}>
            Edit Profile
          </Button>
          <Button variant="outlined" color="error" onClick={handleDelete}>
            Delete Profile
          </Button>
        </div>

        <div className="biodata-detail-container" style={{ marginBottom: '30px' }}>
          <div className="biodata-detail-hero">
            <div className="biodata-detail-images">
              {profile.imagePaths && profile.imagePaths.length > 0 ? (
                profile.imagePaths.length === 1 ? (
                  <div className="biodata-single-image" onClick={() => !imageError && handleImageClick(0)}>
                    <img
                      src={imageError ? getDefaultImage(profile.Gender ?? profile.gender) : profile.imagePaths[0]}
                      onError={() => setImageError(true)}
                      alt={profile['Full Name'] ?? profile.fullName ?? "Profile"}
                    />
                  </div>
                ) : (
                  <div className="biodata-multi-images">
                    <div className="biodata-main-image" onClick={() => !imageError && handleImageClick(0)}>
                      <img
                        src={imageError ? getDefaultImage(profile.Gender ?? profile.gender) : profile.imagePaths[0]}
                        onError={() => setImageError(true)}
                        alt={profile['Full Name'] ?? profile.fullName ?? "Profile"}
                      />
                    </div>
                    <div className="biodata-thumb-strip">
                      {profile.imagePaths.slice(1, 5).map((img, idx) => (
                        <div key={idx} className="biodata-thumb" onClick={() => handleImageClick(idx + 1)}>
                          <img src={img} alt={`${idx + 2}`} />
                          {idx === 3 && profile.imagePaths.length > 5 && (
                            <div className="biodata-thumb-more">+{profile.imagePaths.length - 5}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ) : (
                <div className="biodata-single-image">
                  <img src={getDefaultImage(profile.Gender ?? profile.gender)} alt="Default" />
                </div>
              )}
            </div>

            <div className="biodata-hero-info">
              <h1 className="biodata-hero-name">{profile['Full Name'] ?? profile.fullName ?? "N/A"}</h1>
              <div className="biodata-hero-tags">
                <span>{formatValue("gender", profile.Gender ?? profile.gender)}</span>
                <span>{formatValue("ageRange", profile['Age Range'] ?? profile.ageRange)}</span>
                <span>{formatValue("caste", profile.Caste ?? profile.caste)}</span>
                <span>{formatValue("maritalStatus", profile['Marital Status'] ?? profile.maritalStatus)}</span>
              </div>
              <div className="biodata-hero-status">
                <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    value={profile.status ?? profile.Status ?? 'Created'}
                    label="Status"
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <MenuItem value="Created">Created</MenuItem>
                    <MenuItem value="Reported">Reported</MenuItem>
                    <MenuItem value="Accepted">Accepted</MenuItem>
                    <MenuItem value="Hide">Hide</MenuItem>
                    <MenuItem value="Show">Show</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className="biodata-detail-sections">
            {Object.entries(getProfileSections()).map(([sectionTitle, sectionData]) => {
              const entries = Object.entries(sectionData).filter(([, value]) => value !== undefined && value !== null && value !== '');
              if (entries.length === 0) return null;
              return (
                <div key={sectionTitle} className="biodata-section">
                  <h2 className="biodata-section-title">{sectionTitle}</h2>
                  <div className="biodata-detail-info-grid">
                    {entries.map(([fieldKey, value]) => (
                      <div key={fieldKey} className="biodata-detail-info-item">
                        <span className="biodata-detail-label">{fieldKey}</span>
                        <span className="biodata-detail-value">{formatValue(fieldKey, value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>

    {selectedImageIndex !== null && (
      <div className="biodata-lightbox" onClick={handleCloseImagePopup}>
        <button className="biodata-lightbox-close" onClick={handleCloseImagePopup}>✕</button>
        {allImages.length > 1 && (
          <button className="biodata-lightbox-nav biodata-lightbox-prev" onClick={handlePrevImage}>‹</button>
        )}
        <div className="biodata-lightbox-img-wrap" onClick={(e) => e.stopPropagation()}>
          <img src={allImages[selectedImageIndex]} alt="Profile" />
        </div>
        {allImages.length > 1 && (
          <button className="biodata-lightbox-nav biodata-lightbox-next" onClick={handleNextImage}>›</button>
        )}
        {allImages.length > 1 && (
          <div className="biodata-lightbox-counter">{selectedImageIndex + 1} / {allImages.length}</div>
        )}
      </div>
    )}
  </>
  );
};

export default BiodataDetails;
